import { Injectable } from '@angular/core';

export interface CompressOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0..1, applies to jpeg/webp encoding
  mimeType?: string; // override output type; defaults to the source file's type
}

@Injectable({ providedIn: 'root' })
export class ImageCompressionService {
  async compress(file: File, options: CompressOptions = {}): Promise<File> {
    const { maxWidth = 1600, maxHeight = 1600, quality = 0.75 } = options;

    // Only raster formats benefit from canvas re-encoding
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      return file;
    }

    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxWidth / bitmap.width, maxHeight / bitmap.height);
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const mimeType = options.mimeType ?? file.type;
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), mimeType, quality)
    );

    // Only replace the original if compression actually reduced size
    return blob.size < file.size
      ? new File([blob], file.name, { type: mimeType })
      : file;
  }
}
